import { ErrorOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  InputBase,
  MenuItem,
  Select,
} from "@mui/material";
import axios from "axios";
import React, { useState, useEffect } from "react";
import { ExternalLink, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import CryptoJS from "crypto-js";
import generatecode from "../utils/generateCode";
import decryptApi from "../utils/decryptApi";
import encryptApi from "../utils/encryptApi";
import planData from "../plans.json";
import { getChannelType } from "../../config/channelTypes";
import { isGoogleEmailForAddress, isLikelyValidEmail } from "../../utils/emailProvider";
import FrontChannelSettings from "./FrontChannelSettings";
import {
  canUseFrontVoiceMode,
  getFrontModeForPlan,
  readFrontSettingsFromChannelData,
} from "../../utils/frontChannelSettings";

const EditChannelModal = ({
  open,
  setOpen,
  channels,
  setChannels,
  channel_data,
  closeMenu,
  index,
  channelUpdatedModal,
  setOpenChannelUpdatedModal,
}) => {
  const [loader, setLoader] = useState(false);
  const [error_msg, setErrormsg] = useState(null);
  const [code_sent, setCodeSent] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const navigate = useNavigate();
  const [channel, setChannel] = useState(() => {
    const org = JSON.parse(localStorage.getItem("organization"));
    if (!channel_data?.ch_id?.S) {
      return {
        channel_id: "",
        label: "",
        last_updated: "",
        status: "",
        type: "",
        value: "",
        organization_id: org?.orgId?.S ?? "",
        slack_id: "",
        api_key: "",
        front_id: "",
      };
    }
    return {
      channel_id: channel_data.ch_id.S,
      label: channel_data.label.S,
      last_updated: channel_data.last_updated.S,
      status: channel_data.status.S,
      type: channel_data.type.S,
      value: channel_data.value?.S ?? "",
      organization_id: org?.orgId?.S ?? "",
      slack_id: channel_data.slack_id?.S ?? "",
      api_key: channel_data.api_key?.S ?? "",
      front_id: channel_data.front_id?.S ?? "",
      ...readFrontSettingsFromChannelData(channel_data),
    };
  });

  const [code, setCode] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isGoogleEmail, setIsGoogleEmail] = useState(false);
  const [isCheckingProvider, setIsCheckingProvider] = useState(false);

  const gmailSearchUrl = `https://mail.google.com/mail/u/0/#search/from:hello@twilanswer.com+newer_than:1d`;

  // ElevenLabs: agents and selection (for edit)
  const [elevenlabsAgents, setElevenlabsAgents] = useState([]);
  const [elevenlabsFetching, setElevenlabsFetching] = useState(false);
  const [elevenlabsSelectedIds, setElevenlabsSelectedIds] = useState(() => {
    if (channel_data?.type?.S !== "elevenlabs") return new Set();
    try {
      const ids = JSON.parse(channel_data.value?.S || "[]");
      return new Set(Array.isArray(ids) ? ids : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    setCode(generatecode());
    setCodeSent(false);
    setErrormsg(null);
  }, [channel.type]);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const sub = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription",
          {
            organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
            customer_id: JSON.parse(localStorage.getItem("organization"))
              .subscription_details.M.stripe_cust_id.S,
          }
        );
        setPlanDetails(planData[sub.data?.planDetails[0].plan_id]);
      } catch (error) {
        console.error(
          "EditChannelModal: check subscription failed",
          error.response?.data || error.message || error
        );
      }
    };

    if (open) {
      checkSubscription();
    }
  }, [open]);

  useEffect(() => {
    if (channel?.type !== "email" || !channel.value) {
      setIsGoogleEmail(false);
      setIsCheckingProvider(false);
      return;
    }
    if (!isLikelyValidEmail(channel.value)) {
      setIsGoogleEmail(false);
      setIsCheckingProvider(false);
      return;
    }
    setIsCheckingProvider(true);
    const handle = setTimeout(async () => {
      try {
        const result = await isGoogleEmailForAddress(channel.value);
        setIsGoogleEmail(result);
      } catch {
        setIsGoogleEmail(false);
      } finally {
        setIsCheckingProvider(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [channel?.type, channel?.value]);

  // Reset state when modal opens with (possibly different) channel_data
  useEffect(() => {
    if (open && channel_data?.ch_id?.S) {
      const org = JSON.parse(localStorage.getItem("organization"));
      setChannel({
        channel_id: channel_data.ch_id.S,
        label: channel_data.label.S,
        last_updated: channel_data.last_updated.S,
        status: channel_data.status.S,
        type: channel_data.type.S,
        value: channel_data.value?.S ?? "",
        organization_id: org?.orgId?.S ?? "",
        slack_id: channel_data.slack_id?.S ?? "",
        api_key: channel_data.api_key?.S ?? "",
        front_id: channel_data.front_id?.S ?? "",
        ...readFrontSettingsFromChannelData(channel_data),
      });
      if (channel_data.type?.S === "elevenlabs") {
        try {
          const ids = JSON.parse(channel_data.value?.S || "[]");
          setElevenlabsSelectedIds(new Set(Array.isArray(ids) ? ids : []));
        } catch {
          setElevenlabsSelectedIds(new Set());
        }
        setElevenlabsAgents([]);
      }
    }
  }, [open, channel_data?.ch_id?.S]);

  // Load existing elevenlabs agents from channel_data.agents if available
  useEffect(() => {
    if (channel.type === "elevenlabs" && channel_data?.agents?.L?.length > 0 && elevenlabsAgents.length === 0) {
      const agents = channel_data.agents.L.map((item) => ({
        agent_id: item.M?.id?.S ?? item.M?.agent_id?.S,
        name: item.M?.name?.S ?? "Unknown",
      })).filter((a) => a.agent_id);
      if (agents.length > 0) setElevenlabsAgents(agents);
    }
  }, [channel.type, channel_data?.agents]);

  async function fetchElevenlabsAgents() {
    setErrormsg(null);
    setElevenlabsFetching(true);
    try {
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "elevenlabs/list-agents",
        {
          organization_id: channel.organization_id,
          channel_id: channel.channel_id,
          page_size: 30,
          cursor: null,
          search: null,
          archived: false,
          show_only_owned_agents: false,
          sort_by: "name",
          sort_direction: "asc",
        }
      );
      if (res.data?.status === "success" && Array.isArray(res.data?.agents)) {
        setElevenlabsAgents(res.data.agents);
        setElevenlabsSelectedIds((prev) => {
          const newIds = res.data.agents.map((a) => a.agent_id);
          if (prev.size === 0) return new Set(newIds);
          return new Set(newIds.filter((id) => prev.has(id)));
        });
      } else {
        setErrormsg(res.data?.message || "Failed to fetch agents");
      }
    } catch (error) {
      // Log for production diagnostics when ElevenLabs agents fetch fails
      console.error("EditChannelModal: fetch ElevenLabs agents failed", error.response?.data || error.message || error);
      const backendMsg = error.response?.data?.message || error.message;
      const scopeHint =
        "Your API key must have the Agents scope with at least Read permission (Write is recommended for adding tools).";
      setErrormsg(backendMsg ? `${backendMsg} ${scopeHint}` : scopeHint);
    } finally {
      setElevenlabsFetching(false);
    }
  }

  function toggleElevenlabsAgent(agentId) {
    setElevenlabsSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  }

  useEffect(() => {
    if (timeLeft === 0) {
      setCode(generatecode());
    }

    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(intervalId); // Cleanup on unmount or re-render
  }, [timeLeft]);

  async function sendEmailVerificationLink() {
    setErrormsg(null);
    const ciphertext = CryptoJS.AES.encrypt(
      code,
      process.env.REACT_APP_TWILANSWER_SECRET
    ).toString();
    setLoader(true);
    try {
      let response = await axios.post(
        process.env.REACT_APP_BACKEND_URL +
          "email/send-email-verification-code",
        {
          email: channel.type === "email" && channel.value,
          toName: channel.label,
          code: await encryptApi(code),
        }
      );
      setCodeSent(true);
      setTimeLeft(60);

      setLoader(false);
    } catch (error) {
      // Log for production diagnostics when send verification (email/slack) fails
      console.error("EditChannelModal: send verification failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrormsg(error.response.data.message);
      }
      setLoader(false);
    }
  }

  function decodeToken(token) {
    try {
      const decoded = jwtDecode(token);
      return decoded.api_key;
    } catch (error) {
      // Log when channel token is invalid or malformed
      console.error("Invalid token:", error);
      return null;
    }
  }

  async function sendPhoneVerificationText() {
    setErrormsg(null);

    setLoader(true);
    const ciphertext = CryptoJS.AES.encrypt(
      code,
      process.env.REACT_APP_TWILANSWER_SECRET
    ).toString();
    try {
      let response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "twilio/read-num-aloud",
        {
          organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
          fromNumber: JSON.parse(localStorage.getItem("organization"))
            .twilio_integration?.M?.primary_phone?.S,
          forwarding_number: channel.value,
          code: await encryptApi(code),
        }
      );
      setCodeSent(true);
      setTimeLeft(60);

      setLoader(false);
    } catch (error) {
      // Log for production diagnostics when phone verification (call) fails
      console.error("EditChannelModal: send phone verification call failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrormsg(error.response.data.error);
      }
      setLoader(false);
    }
  }

  async function sendPhoneVerificationSms() {
    setErrormsg(null);
    const ciphertext = CryptoJS.AES.encrypt(
      code,
      process.env.REACT_APP_TWILANSWER_SECRET
    ).toString();
    setLoader(true);
    try {
      let response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "twilio/send-text-message",
        {
          organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
          fromNumber: JSON.parse(localStorage.getItem("organization"))
            .twilio_integration?.M.primary_phone.S,
          toNumber: channel.value,
          code: await encryptApi(code),
        }
      );
      setCodeSent(true);
      setTimeLeft(60);

      setLoader(false);
    } catch (error) {
      // Log for production diagnostics when phone verification (SMS) fails
      console.error("EditChannelModal: send phone verification SMS failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrormsg(error.response.data.error);
      }
      setLoader(false);
    }
  }

  const editChannel = async () => {
    let check = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
      {
        userId: JSON.parse(localStorage.getItem("user")).userId.S,
      }
    );

    if (check.status === 200) {
      const user___ = check.data.user;

      if (
        user___.relatedOrgs.L?.find(
          (e) =>
            e?.M.org_id?.S ===
            JSON.parse(localStorage.getItem("organization")).orgId.S
        )
      ) {
        const elevenlabsValue =
          channel.type === "elevenlabs"
            ? JSON.stringify([...elevenlabsSelectedIds].sort())
            : channel.value;
        if (
          JSON.parse(localStorage.getItem("organization")).channels?.L.find(
            (e) =>
              e.M.type.S === channel.type &&
              e.M.value.S === elevenlabsValue &&
              e.M.value.S !== "N/A" &&
              e.M.type.S !== "front" &&
              e.M.type.S !== "slack" &&
              e.M.ch_id.S !== channel.channel_id
          )
        ) {
          setErrormsg(
            "Another channel with " + channel.type + " already exist"
          );
        } else {
          if (
            channel.type === "front" ||
            channel.type === "slack" ||
            channel.type === "elevenlabs" ||
            channel?.code === code
          ) {
            try {
              setLoader(true);
              const payload =
                channel.type === "elevenlabs"
                  ? (() => {
                      const allAgents =
                        elevenlabsAgents.length > 0
                          ? elevenlabsAgents
                          : (channel_data?.agents?.L || []).map((item) => ({
                              agent_id: item.M?.id?.S ?? item.M?.agent_id?.S,
                              name: item.M?.name?.S ?? "Unknown",
                            }));
                      const selectedIds = [...elevenlabsSelectedIds];
                      const agents = selectedIds
                        .map((id) => {
                          const a = allAgents.find((x) => x.agent_id === id);
                          return { name: a?.name ?? "Unknown", id };
                        })
                        .filter((a) => a.id);
                      const el_exclusion_list =
                        allAgents.length > 0
                          ? allAgents
                              .filter((a) => a.agent_id && !elevenlabsSelectedIds.has(a.agent_id))
                              .map((a) => a.agent_id)
                          : [];
                      return {
                        ...channel,
                        value: JSON.stringify(selectedIds.sort()),
                        api_key: channel.api_key || "",
                        agents,
                        el_exclusion_list,
                      };
                    })()
                  : channel.type === "front"
                    ? {
                        ...channel,
                        frontMode: getFrontModeForPlan(channel.frontMode, planDetails),
                      }
                  : channel;
              const payloadWithOrg = { ...payload, organization_id: payload.organization_id || JSON.parse(localStorage.getItem("organization")).orgId.S };
              let res = await axios.post(
                process.env.REACT_APP_BACKEND_URL + "update-channel",
                payloadWithOrg
              );

              localStorage.setItem(
                "organization",
                JSON.stringify(res.data.updatedItem)
              );

              setChannels(res.data.updatedItem.channels.L);

              if (channel.type === "email") {
                setOpenChannelUpdatedModal(true);
              }

              if (channel.type === "forward_number") {
                setOpenChannelUpdatedModal(true);
              }

              if (channel.type === "sms") {
                setOpenChannelUpdatedModal(true);
              }
              if (channel.type === "front") {
                setOpenChannelUpdatedModal(true);
              }
              if (channel.type === "slack") {
                setOpenChannelUpdatedModal(true);
              }
              if (channel.type === "elevenlabs") {
                setOpenChannelUpdatedModal(true);
              }

              setOpen(false);
              setLoader(false);
              setErrormsg(null);
              closeMenu(index);
            } catch (error) {
              // Log for production diagnostics when edit channel fails
              console.error("EditChannelModal: edit channel failed", error.response?.data || error.message || error);
              if (error.response && error.response.data) {
                setErrormsg(error.response.data.message);
              }
              setLoader(false);
            }
          } else {
            setErrormsg("verification code does not match");
          }
        }
      } else {
        window.location.reload();
      }
    }
  };

  //  useEffect(() => {

  //       const globalKeyListener = (e) => {
  //         console.log("Global key event:", e.key);
  //         if(document.getElementById('input') === document.activeElement){
  //           if (e.key === 'd' || e.key === 'D' || e.key === 'E' || e.key === 'e') {
  //             // Manually update the input value with 'd'
  //             setChannel((prevChannel) => ({
  //               ...prevChannel,
  //               label: prevChannel.label + e.key,
  //             }));
  //             // Prevent the default action of the 'd' key
  //             document.getElementById('input').focus()
  //             e.preventDefault();
  //           }
  //         }
  //       };

  //       document.addEventListener("keydown", globalKeyListener);

  //       return () => {
  //         document.removeEventListener("keydown", globalKeyListener);
  //       };
  //     }, []);

  const channelType = getChannelType(channel.type);

  return (
    <Dialog
      open={open}
      fullWidth={false}
      maxWidth="sm"
      BackdropProps={{
        style: { backgroundColor: "rgba(142, 149, 159, 0.75)" },
      }}
    >
      <div className="px-8 py-6">
        <div className="flex items-center justify-between space-x-4 mb-4">
          <div className="text-[17px] font-semibold text-black">Edit Channel</div>
          <X onClick={() => setOpen(false)} className="cursor-pointer" />
        </div>
        {error_msg && (
          <div className="flex items-center text-red-500 lg:text-[13px] text-[12px] mt-2 font-semibold">
            <ErrorOutline sx={{ mr: 1 }} /> {error_msg}
          </div>
        )}

        <div className="mb-4">
          <div className="lg:text-[14px] text-[13px] font-semibold mb-2">Channel Type</div>
          <div className="flex items-center space-x-2 border-[#E6E7EB] rounded-[10px] pl-4 pr-6 py-3 border-[2px] bg-[#F9FAFB]">
            {channelType.icon}
            <div>
              <div className="font-semibold lg:text-[16px] text-[14px] flex items-center gap-2">
                {channelType.name}
                {channelType.beta && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                    Beta
                  </span>
                )}
              </div>
              <div className="text-[13px]">{channelType.description}</div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="lg:text-[14px] text-[13px] font-semibold flex items-center mb-2">
            Channel Nickname
          </div>
          <InputBase
            id="input"
            value={channel.label}
            onChange={(e) => setChannel({ ...channel, label: e.target.value })}
            placeholder="Enter a nickname for this channel"
            sx={{
              bgcolor: "white",
              border: "1px solid #D1D5DB",
              width: "100%",
              px: 2,
              py: "5px",
              borderRadius: "8px",
              fontSize: { lg: "16px", xs: "14px" },
            }}
          />
        </div>
            {channel.type === "email" && (
              <>
                <div className="mb-4 w-full">
                  <div className="lg:text-[14px] text-[13px] font-semibold mb-2">
                    Email Address
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <InputBase
                        value={channel.value}
                        onChange={(e) =>
                          setChannel({ ...channel, value: e.target.value })
                        }
                        placeholder="Enter email address"
                        sx={{
                          bgcolor: "white",
                          border: "1px solid #D1D5DB",
                          width: "100%",
                          px: 2,
                          py: "5px",
                          borderRadius: "8px",
                          fontSize: { lg: "16px", xs: "14px" },
                        }}
                      />
                    </div>
                    <div className="flex lg:justify-center mt-2 lg:mt-0">
                      {timeLeft >= 1 ? (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                            h: "100%",
                          }}
                        >
                          Expires {timeLeft}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                            h: "100%",
                          }}
                          onClick={sendEmailVerificationLink}
                          disabled={!channel.value}
                        >
                          Verify
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {code_sent && (
                  <>
                    <div className="mb-4">
                      <div className="lg:text-[14px] text-[13px] font-semibold mb-2">
                        Verification Code
                      </div>
                      <InputBase
                        value={channel.code}
                        onChange={(e) =>
                          setChannel({ ...channel, code: e.target.value })
                        }
                        placeholder="Enter verification code"
                        sx={{
                          bgcolor: "white",
                          border: "1px solid #D1D5DB",
                          width: "100%",
                          px: 2,
                          py: "5px",
                          borderRadius: "8px",
                          fontSize: { lg: "16px", xs: "14px" },
                        }}
                      />
                    </div>
                    {isGoogleEmail && (
                      <div className="mb-4">
                        <Button
                          fullWidth
                          onClick={() =>
                            window.open(gmailSearchUrl, "_blank", "noopener,noreferrer")
                          }
                          startIcon={<ExternalLink size={16} />}
                          sx={{
                            textTransform: "none",
                            backgroundColor: "#16a34a",
                            color: "#fff",
                            borderRadius: "10px",
                            py: "8px",
                            fontSize: { lg: "14px", xs: "13px" },
                            "&:hover": { backgroundColor: "#15803d" },
                          }}
                        >
                          Open Gmail
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            {channel.type === "sms" && (
              <>
                <div className="mb-4">
                  <div className="lg:text-[14px] text-[13px] font-semibold mb-2">
                    Phone Number
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <InputBase
                        value={channel.value}
                        onChange={(e) =>
                          setChannel({ ...channel, value: e.target.value })
                        }
                        placeholder="Enter phone number"
                        sx={{
                          bgcolor: "white",
                          border: "1px solid #D1D5DB",
                          width: "100%",
                          px: 2,
                          py: "5px",
                          borderRadius: "8px",
                          fontSize: { lg: "16px", xs: "14px" },
                        }}
                      />
                    </div>
                    <div className="flex lg:justify-center mt-2 lg:mt-0">
                      {timeLeft >= 1 ? (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                            h: "100%",
                          }}
                        >
                          Expires {timeLeft}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                            h: "100%",
                          }}
                          onClick={sendPhoneVerificationSms}
                        >
                          Verify
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {code_sent && (
                  <div className="mb-4">
                    <div className="lg:text-[14px] text-[13px] font-semibold mb-2">
                      Verification Code
                    </div>
                    <InputBase
                      value={channel.code}
                      onChange={(e) =>
                        setChannel({ ...channel, code: e.target.value })
                      }
                      placeholder="Enter verification code"
                      sx={{
                        bgcolor: "white",
                        border: "1px solid #D1D5DB",
                        width: "100%",
                        px: 2,
                        py: "5px",
                        borderRadius: "8px",
                        fontSize: { lg: "16px", xs: "14px" },
                      }}
                    />
                  </div>
                )}
              </>
            )}
            {channel.type === "forward_number" && (
              <>
                <div className="mb-4">
                  <div className="lg:text-[14px] text-[13px] font-semibold mb-2">
                    Phone Number
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <InputBase
                        value={channel.value}
                        onChange={(e) =>
                          setChannel({ ...channel, value: e.target.value })
                        }
                        placeholder="Enter phone number"
                        sx={{
                          bgcolor: "white",
                          border: "1px solid #D1D5DB",
                          width: "100%",
                          px: 2,
                          py: "5px",
                          borderRadius: "8px",
                          fontSize: { lg: "16px", xs: "14px" },
                        }}
                      />
                    </div>
                    <div className="flex lg:justify-center mt-2 lg:mt-0">
                      {timeLeft >= 1 ? (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                            h: "100%",
                          }}
                        >
                          Expires {timeLeft}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                            color="primary"
                            sx={{
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: { lg: "14px", xs: "12px" },
                            h: "100%",
                          }}
                          onClick={sendPhoneVerificationText}
                        >
                          Verify
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {code_sent && (
                  <div className="mb-4">
                    <div className="lg:text-[14px] text-[13px] font-semibold mb-2">
                      Verification Code
                    </div>
                    <InputBase
                      value={channel.code}
                      onChange={(e) =>
                        setChannel({ ...channel, code: e.target.value })
                      }
                      placeholder="Enter verification code"
                      sx={{
                        bgcolor: "white",
                        border: "1px solid #D1D5DB",
                        width: "100%",
                        px: 2,
                        py: "5px",
                        borderRadius: "8px",
                        fontSize: { lg: "16px", xs: "14px" },
                      }}
                    />
                  </div>
                )}
              </>
            )}

            {channel.type === "front" && (
              <div className="mb-4">
                <FrontChannelSettings
                  frontMode={channel.frontMode}
                  canUseVoiceMode={canUseFrontVoiceMode(planDetails)}
                  planLoaded={planDetails != null}
                  onUpgrade={() => navigate("/dashboard/settings/billing")}
                  onChange={({ frontMode }) =>
                    setChannel({ ...channel, frontMode })
                  }
                />
              </div>
            )}

            {channel.type === "elevenlabs" && (
              <>
                <div className="mb-4">
                  <Button
                    variant="contained"
                      color="primary"
                      sx={{
                      py: "7px",
                      px: "20px",
                      borderRadius: "7px",
                      fontSize: { lg: "14px", xs: "12px" },
                    }}
                    onClick={fetchElevenlabsAgents}
                    disabled={elevenlabsFetching}
                  >
                    {elevenlabsFetching ? (
                      <CircularProgress size="1.3rem" sx={{ color: "white" }} />
                    ) : (
                      "Fetch Agents"
                    )}
                  </Button>
                </div>
                {elevenlabsAgents.length > 0 && (
                  <div className="mb-4">
                    <div className="lg:text-[14px] text-[13px] font-semibold mb-2">
                      Select agents to make available
                    </div>
                    <Box
                      sx={{
                        maxHeight: 240,
                        overflowY: "auto",
                        border: "1px solid #D1D5DB",
                        borderRadius: "8px",
                        p: 1,
                        bgcolor: "#F9FAFB",
                      }}
                    >
                      {elevenlabsAgents.map((agent) => (
                        <FormControlLabel
                          key={agent.agent_id}
                          control={
                            <Checkbox
                              checked={elevenlabsSelectedIds.has(agent.agent_id)}
                              onChange={() => toggleElevenlabsAgent(agent.agent_id)}
                              size="small"
                            />
                          }
                          label={
                            <span className="text-[14px]">
                              {agent.name}
                              {agent.tags?.length > 0 && (
                                <span className="text-[12px] text-gray-500 ml-1">
                                  ({agent.tags.join(", ")})
                                </span>
                              )}
                            </span>
                          }
                          sx={{ display: "block", m: 0, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </div>
                )}
              </>
            )}

            <div className="flex items-center justify-between space-x-4 mt-5">
              <Button
                sx={{
                  textTransform: "none",
                  border: "1px solid #E6E7EB",
                  color: "black",
                  py: "7px",
                  px: "20px",
                  borderRadius: "6px",
                  fontSize: { lg: "14px", xs: "12px" },
                }}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                  color="primary"
                  sx={{
                  py: "4px",
                  px: "40px",
                  borderRadius: "7px",
                  fontSize: { lg: "14px", xs: "12px" },
                }}
                onClick={editChannel}
                disabled={
                  loader ||
                  (channel.type === "elevenlabs" &&
                    elevenlabsSelectedIds.size === 0 &&
                    elevenlabsAgents.length > 0)
                }
              >
                {loader ? (
                  <CircularProgress size="1.3rem" sx={{ color: "white" }} />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
      </div>
    </Dialog>
  );
};

export default EditChannelModal;
